import { getAuthProviderFetchJson } from './Auth';
import { superAdminApiServer } from './config';

export interface CompanySummary {
  uuid: string;
  name: string | null;
  /** Set on every company the list endpoint returns; a company named only by its uuid has none yet. */
  id?: number;
}

export const COMPANY_PAGE = 20;

export interface CompanyPage {
  companies: CompanySummary[];
  /** Every company the filter matches; the list endpoint says it in Content-Range. */
  total: number | null;
}

/** The one call behind every company lookup: a page of the companies matching the filter, sorted by name. */
export const listCompanies = async (filter: Record<string, unknown>, offset = 0, size = COMPANY_PAGE): Promise<CompanyPage> => {
  const search = new URLSearchParams({ filter: JSON.stringify(filter), range: `[${offset},${offset + size - 1}]`, sort: '["name","ASC"]' });
  const { json, headers } = await getAuthProviderFetchJson(`${superAdminApiServer}/companies?${search.toString()}`);
  const companies = Array.isArray(json) ? json.map((company: { id?: number; uuid: string; name?: string | null }) => ({ id: company.id, uuid: company.uuid, name: company.name ?? null })) : [];
  const range = Number.parseInt(headers?.get?.('content-range') ?? '', 10);
  return { companies, total: Number.isNaN(range) ? null : range };
};

/** The company with this uuid, or null when there is none. */
export const findCompany = async (uuid: string): Promise<CompanySummary | null> => (await listCompanies({ uuid }, 0, 1)).companies[0] ?? null;

/** The company with this id, or null when there is none. */
export const findCompanyById = async (id: number): Promise<CompanySummary | null> => (await listCompanies({ id }, 0, 1)).companies[0] ?? null;

/** Every company with one of these ids, in one request however many there are. */
export const findCompaniesById = async (ids: number[]): Promise<CompanySummary[]> => (await listCompanies({ id: ids }, 0, ids.length)).companies;

/** A page of the companies whose name contains the text, whatever its case; "*" skips any text. */
export const searchCompanies = (name: string, offset = 0): Promise<CompanyPage> => listCompanies({ name }, offset);
